// ============================================================================
// RÉFÉRENTIELS — ONGLET « GRILLES DE TAILLES », en DEUX panneaux : les tailles, et le second axe.
//
// ⚠️ L'ORDRE EST UNE DONNÉE, PAS UN AFFICHAGE, et c'est toute la raison d'être de cet écran. Il pilote
// le tri des matrices de stock, l'ordre des colonnes d'inventaire et la lecture des étiquettes. Rangée
// par ordre alphabétique, une grille donnerait L avant M et S avant XS — d'où une saisie manuelle de
// l'ordre, jamais un tri automatique.
//
// ⚠️ LE SECOND AXE EST UNE GRILLE SECONDAIRE (panneau « Tailles 2 ») — même table, même entité, un axe
// différent — parce qu'il porte son propre LIBELLÉ de rubrique : « Longueur » pour un jean, « Bonnet »
// pour un soutien-gorge.
//
// ⚠️ AUCUNE GRILLE N'EN RATTACHE UNE AUTRE, ET LA MAQUETTE AVAIT RAISON DE N'EN DESSINER AUCUN
// CONTRÔLE. Un sélecteur « Dimension secondaire » a vécu ici, sur la carte de chaque grille de
// tailles : il écrivait `size_grid.grid2_id`, donc il donnait ce second axe à TOUS les modèles portant
// la grille — un pantalon donnait ses longueurs aux tee-shirts. C'est l'ARTICLE qui apparie ses deux
// axes, et ce choix vit désormais dans la fiche, à côté de sa grille de tailles. Ce qui reste ici est
// ce qui appartient vraiment au référentiel : créer une dimension, la nommer, ordonner ses valeurs.
//
// ⚠️ LES LISTES PARTENT EN TABLEAU, JAMAIS EN TEXTE JSON. Cet écran envoyait `sizes` sérialisé — et le
// producteur, qui exige une liste, refusait CHAQUE écriture de grille depuis le premier jour, avec son
// motif réel en toast. Une garde le fige désormais.
//
// ⚠️ LE GLISSER VIENT DU MODULE PARTAGÉ (`useTileDrag`) et DOUBLE les flèches ↑/↓ : les deux gestes
// passent par la MÊME fonction de déplacement, sinon celui qu'on teste le moins finit par diverger.
//
// ⚠️ CE QUI SE REFUSE SE REFUSE CÔTÉ SERVEUR, avec son motif réel : retirer une taille ou une valeur
// que des articles portent ne supprimerait aucune déclinaison, mais la grille cesserait de la
// connaître — la matrice perdrait sa colonne ou sa ligne. L'écran n'anticipe pas ce refus.
// ============================================================================
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { confirm, showToast, useTileDrag, checkLabel, Segmented, Trans } from '@orgazone/ui-web';
import { api, type ReferentialDimension, type ReferentialGrid, type ReferentialGrids } from '../api';
import { parseDimensionValues } from './referentiels-grilles-values';
import { t } from '../i18n';

/** Les deux panneaux de l'onglet — les clés sont celles du dessin (`data-fggrsub`). */
type Panneau = 'g1' | 'g2';

export function OngletGrilles({ jeu, failure, reload }: {
  jeu: ReferentialGrids | null;
  failure: string | null;
  reload: () => void;
}) {
  const [panneau, setPanneau] = useState<Panneau>('g1');
  const [composeur, setComposeur] = useState<string | null>(null);

  /** Écrit une grille (nom + tailles), puis RELIT — c'est le producteur qui a le dernier mot. */
  const write = useCallback(async (name: string, sizes: readonly string[], intention: string) => {
    try {
      await api.createReferentielEntry('size_grid', { name, sizes }, intention);
      reload();
      return true;
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « la taille L porte du stock » se corrige en désactivant des déclinaisons,
      // pas en recliquant. Un « échec » générique enverrait recliquer.
      showToast(e instanceof Error ? e.message : String(e), 'err');
      reload();
      return false;
    }
  }, [reload]);

  if (failure !== null) {
    return (
      <div style={{
        padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)',
        border: '1px solid var(--rose-fg)', color: 'var(--rose-fg)', fontSize: '12.5px', lineHeight: 1.5,
      }} role="alert">
        {t('Les grilles n’ont pas pu être lues : {failure}', { failure })}
      </div>
    );
  }
  if (jeu === null) {
    return <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture des grilles…')}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      {/* Relevé du dessin : deux panneaux exclusifs, les tailles d'abord. */}
      <div style={{ display: 'flex' }}>
        <Segmented<Panneau>
          options={[{ id: 'g1', label: t('Tailles') }, { id: 'g2', label: t('Tailles 2 — dimensions secondaires') }]}
          value={panneau}
          onChange={setPanneau}
          label={t('Panneau du référentiel des grilles')}
          size="md"
        />
      </div>

      {panneau === 'g2' ? (
        <PanneauDimensions dimensions={jeu.dimensions} reload={reload} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13.5px', color: 'var(--ink-600)' }}>
              <Trans k='<1>{n}</1> grille' vars={{ n: jeu.rows.length }}
                parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
            </span>
            <div style={{ flex: 1 }} />
            {composeur === null && (
              <button
                type="button" onClick={() => setComposeur('')}
                style={{
                  height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
                  background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
                
                {t('Nouvelle grille')}
              </button>
            )}
          </div>

          {composeur !== null && (
            <FormuleNouvelleGrille
              valeur={composeur}
              onChange={setComposeur}
              existing={jeu.rows.map((g) => g.name)}
              onCancel={() => setComposeur(null)}
              onCreate={async () => {
                const nom = composeur.trim();
                if (nom === '') return;
                // ⚠️ CRÉÉE VIDE, et l'écran le dit : ses tailles s'ajoutent ensuite, dans l'ordre. Proposer
                // une grille pré-remplie ferait accepter un ordre que personne n'a choisi.
                if (await write(nom, [], `grid:${nom}`)) setComposeur(null);
              }}
            />
          )}

          {jeu.rows.map((g) => (
            <CarteGrille key={g.name} g={g} write={write} reload={reload} />
          ))}

          {jeu.rows.length === 0 && composeur === null && (
            <div style={{
              padding: '28px 22px', borderRadius: 'var(--r-md)', border: '1px dashed var(--ink-300)',
              background: 'var(--ink-50)', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-500)',
            }}>
              
              {t('Aucune grille de tailles dans ce magasin.')}
            </div>
          )}

          {jeu.truncated && (
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', fontWeight: 600 }}>
              {t('Liste bornée à {n} grille — d’autres existent au-delà.', { n: jeu.limit })}</div>
          )}

          {/* Relevé mot pour mot : c'est cette note qui explique pourquoi l'ordre se saisit à la main. */}
          <div style={{
            display: 'flex', gap: 10, padding: '14px 16px', borderRadius: 'var(--r-sm)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          }}>
            <span style={{ color: 'var(--amber-fg)', flexShrink: 0, display: 'flex', paddingTop: 1 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" aria-hidden>
                <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
              </svg>
            </span>
            <span style={{ fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.6 }}>
              <Trans k='<1>L’ordre est une donnée, pas un affichage.</1> Il pilote le tri des matrices de stock, l’ordre des colonnes dans les inventaires et la lecture des étiquettes. Une grille rangée par ordre alphabétique donnerait L avant M et S avant XS — d’où la saisie manuelle de l’ordre, jamais un tri automatique. Retirer une taille utilisée par des déclinaisons est refusé : il faut d’abord les désactiver.' parts={[(x) => <strong>{x}</strong>]} /></span>
          </div>
        </>
      )}
    </div>
  );
}

/** La bande de création — elle reconnaît une grille qui existe déjà plutôt que d'en faire une seconde. */
function FormuleNouvelleGrille({ valeur, onChange, existing, onCreate, onCancel }: {
  valeur: string;
  onChange: (v: string) => void;
  existing: readonly string[];
  onCreate: () => void;
  onCancel: () => void;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const verdict = checkLabel(valeur, existing);
  const ready = verdict.etat === 'neuf' || verdict.etat === 'proche';

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      padding: '14px 16px',
    }}>
      <div id={`${fieldId}-0`} style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
        color: 'var(--ink-500)', marginBottom: 8,
      }}>{t('Nouvelle grille de tailles')}</div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input aria-labelledby={`${fieldId}-0`}
          value={valeur} onChange={(e) => onChange(e.target.value)} autoFocus
          placeholder={t('Pointures enfant, Tailles US…')}
          onKeyDown={(e) => { if (e.key === 'Enter' && ready) onCreate(); }}
          style={{
            height: 38, padding: '0 12px', flex: 1, minWidth: 220, boxSizing: 'border-box',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
            color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none',
          }}
        />
        <button
          // ⚠️ LE CLIC REFUSE CE QUE LA TOUCHE ENTRÉE REFUSE : sans ce garde, une grille VIDE s'écrivait sous le nom d'une
          //    grille existante, que le bouton annonçait pourtant inactif.
          type="button" onClick={() => { if (ready) onCreate(); }} aria-disabled={!ready || undefined}
          style={{
            height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: ready ? 'var(--teal-500)' : 'var(--ink-200)',
            color: ready ? '#fff' : 'var(--ink-400)', fontSize: '13.5px', fontWeight: 700,
            cursor: ready ? 'pointer' : 'not-allowed',
          }}
        >{t('Créer')}</button>
        <button
          type="button" onClick={onCancel}
          style={{
            height: 38, padding: '0 14px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)',
            fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
          }}
        >{t('Annuler')}</button>
      </div>

      {verdict.etat === 'exact' && (
        <div style={{
          marginTop: 10, padding: '11px 13px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)',
          fontSize: '12.5px', color: 'var(--ink-800)',
        }}>
          {t('« {candidat} » existe déjà — modifiez-la plutôt que d’en créer une seconde.', { candidat: verdict.candidat })}</div>
      )}
      {verdict.etat === 'proche' && (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)',
          fontSize: '12.5px', color: 'var(--ink-800)',
        }}>
          {t('Ressemble à « {candidat} » — {pourcent} %. Deux grilles proches se confondent à l’usage.', { candidat: verdict.candidat, pourcent: verdict.pourcent })}</div>
      )}

      <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 9, lineHeight: 1.5 }}>
        
        {t('La grille sera vide : ajoutez ses tailles dans l’ordre, du plus petit au plus grand.')}
      </div>
    </div>
  );
}

type EcrireGrille = (name: string, sizes: readonly string[], intention: string) => Promise<boolean>;

function CarteGrille({ g, write, reload }: {
  g: ReferentialGrid;
  write: EcrireGrille;
  reload: () => void;
}) {

  const [nouvelle, setNouvelle] = useState('');
  const champNouvelle = useRef<HTMLInputElement | null>(null);
  /** L'écriture en cours — Entrée puis un clic ailleurs déclencheraient sinon DEUX fois le même ajout. */
  const ajoutEnCours = useRef(false);

  /**
   * AJOUTE LA TAILLE SAISIE, PUIS REND LA MAIN AU CHAMP.
   *
   * ⚠️ UN SEUL CHEMIN POUR TROIS GESTES (Entrée, clic sur « Ajouter », sortie du champ) : ils écrivaient
   * la même chose à trois endroits, et le traitement des cas limites y divergeait déjà — une valeur
   * déjà présente vidait le champ par le blur et le laissait plein par le bouton.
   * ⚠️ UNE VALEUR DÉJÀ DANS LA GRILLE NE DISPARAÎT PLUS EN SILENCE : le champ la garde et l'écran le
   * DIT. Effacée sans un mot, l'opérateur croit l'avoir ajoutée — et ne le découvre qu'à l'inventaire.
   */
  const ajouterTaille = useCallback(async (): Promise<void> => {
    if (ajoutEnCours.current) return;
    const saisie = nouvelle.trim();
    if (saisie === '') { setNouvelle(''); return; }
    if (g.sizes.includes(saisie)) {
      showToast(t('« {saisie} » est déjà dans la grille « {name} ».', { saisie, name: g.name }), 'info');
      champNouvelle.current?.focus();
      return;
    }
    ajoutEnCours.current = true;
    try {
      // ⚠️ AJOUTÉE EN FIN DE LISTE, jamais insérée « au bon endroit » : l'ordre est décidé par
      // l'humain, et deviner sa place reviendrait au tri automatique que cet écran refuse.
      if (await write(g.name, [...g.sizes, saisie], `sizes:${g.name}`)) setNouvelle('');
    } finally {
      ajoutEnCours.current = false;
    }
    // ⚠️ APRÈS L'ÉCRITURE, PAS AVANT : la liste est relue, donc l'arbre peut se reconstruire — c'est
    //    à ce moment-là que le focus se perdrait, et le geste suivant demanderait un clic.
    champNouvelle.current?.focus();
  }, [g.name, g.sizes, nouvelle, write]);
  /** Une grille que personne ne porte peut partir ; portée, elle se verrouille. */
  const libre = g.models === 0;

  /**
   * DÉPLACER UNE TAILLE — l'unique fonction, appelée par le glisser comme par les flèches.
   * ⚠️ Deux arithmétiques finiraient par diverger, et c'est le clavier qui perdrait : personne ne le
   * teste à la main.
   */
  const deplacer = useCallback((ordre: readonly string[]) => {
    void write(g.name, [...ordre], `sizes:${g.name}`);
  }, [write, g.name]);

  const drag = useTileDrag({
    keys: g.sizes,
    axis: 'y',
    onReorder: deplacer,
    ghostScope: 'ref-grille',
  });

  const rangs = useMemo(() => new Map(drag.order.map((s, i) => [s, i])), [drag.order]);

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      overflow: 'hidden',
    }} className="ref-grille">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px',
        borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{ flex: 1, minWidth: 150 }}>
          <span style={{
            display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)',
          }}>{g.name}</span>
          {/* ⚠️ LES FAMILLES SONT DÉRIVÉES des articles qui portent la grille : notre schéma ne connaît
              pas de rattachement grille↔famille, contrairement à la maquette. L'écran dit la même
              chose sans inventer une table. */}
          <span style={{
            display: 'block', fontSize: 'var(--t-dense)', marginTop: 1,
            color: g.families.length > 0 ? 'var(--ink-500)' : 'var(--ink-400)',
          }}>
            {g.families.length > 0 ? t('Utilisée par {v}', { v: g.families.join(' · ') }) : t('Aucune famille rattachée')}
          </span>
        </span>

        <span style={{
          display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px',
          borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)',
          fontSize: 'var(--t-meta)', fontWeight: 700, whiteSpace: 'nowrap',
        }}>{t('{n} taille', { n: g.sizes.length })}</span>

        {libre ? (
          <button
            type="button" aria-label={t('Supprimer la grille {name}', { name: g.name })}
            title={t('Supprimer la grille — aucun article ne la porte')}
            onClick={async () => {
              const accord = await confirm({
                title: t('Supprimer la grille « {name} » ?', { name: g.name }),
                message: t('Aucun article ne la porte. Elle disparaîtra du référentiel et des caisses. Le serveur vérifie ce compte au moment de supprimer.'),
                confirmLabel: t('Supprimer'),
              });
              if (!accord) return;
              try {
                await api.deleteReferentielEntry('size_grid', { name: g.name }, `del:grid:${g.name}`);
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), 'err');
              }
              // ⚠️ ON RELIT, ON NE RÉÉCRIT PAS : rappeler `ecrire` ici RECRÉERAIT la grille qu’on
              // vient de supprimer — le producteur ne fait pas la différence entre une création et une
              // écriture de confort.
              reload();
            }}
            style={{
              width: 32, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
              background: 'var(--paper)', color: 'var(--ink-500)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" aria-hidden>
              <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
            </svg>
          </button>
        ) : (
          <span
            title={t('{n} article porte cette grille — elle ne peut pas être supprimée', { n: g.models })}
            style={{
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--ink-500)', flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" aria-hidden>
              <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
          </span>
        )}
      </div>

      <div style={{ padding: '14px 18px' }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
          color: 'var(--ink-500)', marginBottom: 9,
        }}>{t('Ordre des tailles — glissez pour réorganiser')}</div>

        {g.sizes.length === 0 ? (
          <div style={{
            padding: '18px 14px', textAlign: 'center', border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.5,
          }}>
            <Trans k='Aucune taille. Ajoutez-les ci-dessous, <1>du plus petit au plus grand</1>.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {drag.order.map((s) => (
              s === drag.grabbed ? <div key={s}>{drag.placeholder}</div> : (
                <LigneTaille
                  key={s} taille={s} rank={rangs.get(s) ?? 0} total={drag.order.length}
                  drag={drag}
                  onDeplacer={(pas) => {
                    // ⚠️ MÊME FONCTION QUE LE GLISSER : les flèches produisent l'ordre voulu et le
                    // passent au même chemin d'écriture, à la ligne près.
                    const i = drag.order.indexOf(s);
                    const j = i + pas;
                    if (j < 0 || j >= drag.order.length) return;
                    const suite = [...drag.order];
                    suite.splice(i, 1);
                    suite.splice(j, 0, s);
                    deplacer(suite);
                  }}
                  onRetirer={() => void write(g.name, g.sizes.filter((x) => x !== s), `sizes:${g.name}`)}
                />
              )
            ))}
          </div>
        )}
        {drag.ghost}

        {/* ⚠️ CE QUI EST DÉJÀ HORS CHAMP, ET QUE PERSONNE NE VOYAIT. Une déclinaison en 3XL sur une
            grille qui s’arrête à XXL est invisible en matrice et en inventaire — aujourd’hui, sans
            qu’aucune erreur ne soit levée. Une garde empêche d’en fabriquer de nouvelles ; elle ne
            montre pas celles que la reprise a laissées. C’est ce bloc qui les montre, et qui rend
            leur réintégration à un clic. */}
        {g.orphanSizes.length > 0 && (
          <div style={{
            marginTop: 11, padding: '11px 13px', borderRadius: 'var(--r-ctrl)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          }}>
            <div style={{ fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.55 }}>
              <Trans k='<1>Hors champ</1> — des articles portent {n} taille que la grille ne connaît pas : elle n’apparaît ni en matrice de stock, ni en inventaire.'
                vars={{ n: g.orphanSizes.length }} parts={[(x) => <strong>{x}</strong>]} />
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 9 }}>
              {g.orphanSizes.map((s2) => (
                <button
                  key={s2} type="button"
                  title={t('Réintégrer « {s2} » à la fin de la grille', { s2 })}
                  onClick={() => void write(g.name, [...g.sizes, s2], `sizes:${g.name}`)}
                  style={{
                    height: 30, padding: '0 11px', border: '1px solid var(--amber-fg)',
                    borderRadius: 'var(--r-pill)', background: 'var(--paper)', color: 'var(--amber-fg)',
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {s2}
                  <span style={{ fontFamily: 'inherit', fontWeight: 600 }}>{t('+ réintégrer')}</span>
                </button>
              ))}
            </div>
            {/* ⚠️ RÉINTÉGRÉE EN FIN DE LISTE, jamais « à sa place » : deviner où va un 3XL reviendrait
                au tri automatique que cet écran refuse. On la remonte ensuite à la main. */}
            <div style={{ fontSize: '11.5px', color: 'var(--ink-600)', marginTop: 7 }}>
              
              {t('Réintégrée en fin de liste — remontez-la ensuite à sa place.')}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
          <input
            ref={champNouvelle}
            value={nouvelle} onChange={(e) => setNouvelle(e.target.value)}
            placeholder={t('Ajouter une taille — Entrée pour valider')}
            aria-label={t('Ajouter une taille à « {name} »', { name: g.name })}
            // ⚠️ ENTRÉE ÉCRIT, ET LE CURSEUR RESTE. Le chemin d'avant faisait `blur()` — c'était le
            //    BLUR qui écrivait — donc chaque taille coûtait un clic pour revenir dans le champ.
            //    Une grille se saisit en RAFALE (« 38 » Entrée « 40 » Entrée « 42 »…) : rendre la main
            //    au champ n'est pas un confort, c'est la différence entre saisir une grille et la subir.
            //    `preventDefault` parce qu'Entrée dans un champ soumet le formulaire qui l'entoure.
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void ajouterTaille(); } }}
            // ⚠️ LE BLUR ÉCRIT TOUJOURS, et c'est un second chemin assumé : quitter le champ après
            //    avoir tapé une valeur ne doit pas la perdre. Les deux passent par la MÊME fonction,
            //    qui se garde d'écrire deux fois.
            onBlur={() => { void ajouterTaille(); }}
            style={{
              height: 38, padding: '0 12px', flex: 1, minWidth: 0, boxSizing: 'border-box',
              border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
              color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => { void ajouterTaille(); }}
            style={{
              height: 38, padding: '0 14px', border: '1px solid var(--teal-400)',
              borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)',
              fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
            }}
          >{t('Ajouter')}</button>
        </div>
      </div>
    </div>
  );
}

/** Une taille dans son ordre — poignée de glisser, rang, libellé, flèches, retrait. */
function LigneTaille({ taille, rank, total, drag, onDeplacer, onRetirer }: {
  taille: string;
  rank: number;
  total: number;
  drag: ReturnType<typeof useTileDrag>;
  onDeplacer: (pas: number) => void;
  onRetirer: () => void;
}) {
  const carre = {
    width: 32, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
    background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, cursor: 'pointer',
  } as const;

  return (
    <div
      {...drag.tile(taille)}
      style={{
        ...drag.tile(taille).style,
        display: 'flex', alignItems: 'center', gap: 9, height: 44, padding: '0 10px 0 8px',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
      }}
    >
      <span
        {...drag.handle(taille)}
        style={{ ...drag.handle(taille).style, color: 'var(--ink-400)', display: 'flex', flexShrink: 0 }}
        aria-hidden
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round">
          <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" />
        </svg>
      </span>
      {/* Le rang est AFFICHÉ parce qu'il est la donnée : sans lui, réordonner se fait à l'aveugle. */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--ink-400)', width: 18,
        flexShrink: 0, fontVariantNumeric: 'tabular-nums',
      }}>{rank + 1}</span>
      <span style={{
        flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-control)', fontWeight: 700,
        color: 'var(--ink-900)',
      }}>{taille}</span>

      {rank > 0
        ? (
          <button type="button" style={carre} onClick={() => onDeplacer(-1)} aria-label={t('Monter {taille}', { taille })}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 14l6-6 6 6" />
            </svg>
          </button>
        )
        : <span style={{ width: 32, flexShrink: 0 }} />}
      {rank < total - 1
        ? (
          <button type="button" style={carre} onClick={() => onDeplacer(1)} aria-label={t('Descendre {taille}', { taille })}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 10l6 6 6-6" />
            </svg>
          </button>
        )
        : <span style={{ width: 32, flexShrink: 0 }} />}

      <button type="button" style={carre} onClick={onRetirer} aria-label={t('Retirer la taille {taille}', { taille })}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.2" strokeLinecap="round" aria-hidden>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}

// ── PANNEAU « TAILLES 2 » — les dimensions secondaires ──────────────────────────────────────────

function PanneauDimensions({ dimensions, reload }: {
  dimensions: readonly ReferentialDimension[];
  reload: () => void;
}) {
  const [formOn, setFormOn] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 220 }}>
          <span style={{
            display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)',
          }}>{t('Dimensions secondaires — taille 2')}</span>
          <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 2 }}>
            
            {t('Longueurs de jambe aujourd’hui, largeur ou hauteur demain — une grille de tailles s’y rattache, les articles héritent')}
          </span>
        </span>
        {!formOn && (
          <button
            type="button" onClick={() => setFormOn(true)}
            style={{
              height: 38, padding: '0 15px', border: '1.5px solid var(--violet-fg)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontSize: '13.5px', fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
            
            {t('Nouvelle dimension')}
          </button>
        )}
      </div>

      {formOn && (
        <FormuleNouvelleDimension
          existing={dimensions.map((d) => d.name)}
          onClose={() => setFormOn(false)}
          onCreated={() => { setFormOn(false); reload(); }}
        />
      )}

      {dimensions.map((d) => <CarteDimension key={d.name} d={d} reload={reload} />)}

      {dimensions.length === 0 && !formOn && (
        <div style={{
          padding: '28px 22px', borderRadius: 'var(--r-md)', border: '1px dashed var(--ink-300)',
          background: 'var(--ink-50)', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-500)',
        }}>
          
          {t('Aucune dimension secondaire dans ce magasin.')}
        </div>
      )}
    </div>
  );
}

/** Le formulaire de création — nom, libellé d'axe, valeurs ordonnées ; relevé du dessin. */
function FormuleNouvelleDimension({ existing, onClose, onCreated }: {
  existing: readonly string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [nom, setNom] = useState('');
  const [libelle, setLibelle] = useState('');
  const [valeurs, setValeurs] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const verdict = checkLabel(nom, existing);

  const creer = async () => {
    const name = nom.trim();
    const values = parseDimensionValues(valeurs);
    if (name === '') { setErreur(t('Le nom est obligatoire.')); return; }
    if (verdict.etat === 'exact') { setErreur(t('« {candidat} » existe déjà — modifiez-la plutôt que d’en créer une seconde.', { candidat: verdict.candidat })); return; }
    if (values.length === 0) { setErreur(t('Au moins une valeur, séparées par des virgules.')); return; }
    try {
      // ⚠️ UNE GRILLE COMME LES AUTRES, D'AXE SECONDAIRE : même entité, même producteur, mêmes lignes
      //    `size`. Le nom est son identité ; la rubrique est ce qui justifie l'axe. Une seconde entité
      //    pour le second axe aurait été deux fois la même chose, sous deux noms.
      await api.createReferentielEntry(
        'size_grid',
        { name, axis: 'secondary', dimensionLabel: libelle.trim() || null, sizes: values },
        `grid:new:${name}`,
      );
      onCreated();
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL du producteur, dans le formulaire — pas un toast qui disparaît.
      setErreur(e instanceof Error ? e.message : String(e));
    }
  };
  const champ = {
    height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
    background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13px', outline: 'none', minWidth: 0,
    boxSizing: 'border-box',
  } as const;

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--violet-fg)', borderRadius: 'var(--r-md)', padding: '16px 18px',
    }}>
      <div id={`${fieldId}-0`} style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase',
        color: 'var(--ink-500)', marginBottom: 9,
      }}>{t('Nouvelle dimension secondaire')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
        <input aria-labelledby={`${fieldId}-0`} value={nom} onInput={(e) => { setNom(e.currentTarget.value); setErreur(null); }} autoFocus
          placeholder={t('Nom — ex. Longueurs jeans')} style={champ}
          onKeyDown={(e) => { if (e.key === 'Enter') void creer(); }} />
        <input value={libelle} onInput={(e) => { setLibelle(e.currentTarget.value); setErreur(null); }}
          placeholder={t('Libellé d’axe — ex. Longueur')} style={champ}
          onKeyDown={(e) => { if (e.key === 'Enter') void creer(); }} />
        <input value={valeurs} onInput={(e) => { setValeurs(e.currentTarget.value); setErreur(null); }}
          placeholder={t('Valeurs — ex. L30, L32, L34')} style={champ}
          onKeyDown={(e) => { if (e.key === 'Enter') void creer(); }} />
      </div>
      {verdict.etat === 'proche' && erreur === null && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)', fontSize: '12.5px', color: 'var(--ink-800)' }}>
          {t('Ressemble à « {candidat} » — {pourcent} %. Deux dimensions proches se confondent à l’usage.', { candidat: verdict.candidat, pourcent: verdict.pourcent })}</div>
      )}
      {erreur !== null && (
        <div role="alert" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: '12.5px' }}>
          {erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={() => void creer()}
          style={{
            height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: 'var(--violet-fg)', color: '#fff', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer',
          }}>{t('Créer la dimension')}</button>
        <button type="button" onClick={onClose}
          style={{
            height: 38, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
            background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
          }}>{t('Annuler')}</button>
      </div>
      <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 9, lineHeight: 1.5 }}>
        
        {t('Valeurs séparées par des virgules, dans l’ordre d’affichage. Le libellé d’axe apparaît sur les matrices (« Longueur ↓ »).')}
      </div>
    </div>
  );
}

/** Une dimension : son nom, sa rubrique, ce qui s'y rattache, ses valeurs — et le geste qu'elle autorise. */
function CarteDimension({ d, reload }: { d: ReferentialDimension; reload: () => void }) {
  const libre = d.links.length === 0;
  // ⚠️ « EMPLOYÉE AVEC », PAS « RATTACHÉE À » : plus aucune grille ne la déclare — ce sont des MODÈLES
  //    qui l'apparient à leur grille de tailles, et la liste dit avec lesquelles.
  const rattachement = libre
    ? t('Employée par aucun modèle')
    : d.models > 0
      ? t('Employée avec {liste} — {compte}', { liste: d.links.join(', '), compte: t('{n} modèle', { n: d.models }) })
      : t('Employée avec {liste}', { liste: d.links.join(', ') });

  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px',
        borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap',
      }}>
        <span style={{ flex: 1, minWidth: 150 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>{d.name}</span>
            <span style={{
              height: 20, display: 'inline-flex', alignItems: 'center', padding: '0 8px', borderRadius: 'var(--r-pill)',
              background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontFamily: 'var(--font-mono)',
              fontSize: '10px', fontWeight: 700, letterSpacing: '.04em',
            }}>{d.dimensionLabel} ↓</span>
          </span>
          {/* ⚠️ DÉRIVÉ DES ARTICLES par le serveur : une dimension ne déclare jamais elle-même qui
              l'emploie, et plus aucune grille ne le déclare non plus. */}
          <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)', marginTop: 1 }}>{rattachement}</span>
        </span>
        {libre ? (
          <button
            type="button" aria-label={t('Supprimer la dimension « {name} »', { name: d.name })}
            onClick={async () => {
              const accord = await confirm({
                title: t('Supprimer « {name} » ?', { name: d.name }),
                message: t('Aucun modèle ne l’emploie — suppression sans impact. Le serveur vérifie ce compte au moment de supprimer.'),
                confirmLabel: t('Supprimer'),
              });
              if (!accord) return;
              try {
                // Même entité qu'une grille de tailles : le serveur mesure qui la rattache avant de la retirer.
                await api.deleteReferentielEntry('size_grid', { name: d.name }, `del:grid:${d.name}`);
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), 'err');
              }
              reload();
            }}
            style={{
              width: 32, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
              background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        ) : (
          <span role="img" aria-label={t('Dimension employée par des modèles — suppression impossible')}
            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-500)', flexShrink: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '13px 18px' }}>
        {d.values.map((v) => (
          <span key={v} style={{
            height: 30, display: 'inline-flex', alignItems: 'center', padding: '0 12px', borderRadius: 'var(--r-ctrl)',
            background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontFamily: 'var(--font-mono)',
            fontSize: '12.5px', fontWeight: 700,
          }}>{v}</span>
        ))}
        {d.values.length === 0 && (
          <span style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune valeur.')}</span>
        )}
      </div>
    </div>
  );
}
